import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Users, QrCode } from 'lucide-react';
import { UserRole } from '@/types/user';
import InternalUsernameField from './InternalUsernameField';
import InternalPasswordSection from './InternalPasswordSection';
import InternalPinSection from './InternalPinSection';
import ReferralCodeInput from './ReferralCodeInput';
import { QRCodeScanner } from './QRCodeScanner';
import StateSelector from './StateSelector';
import MinorCommunicationRulesAlert from './MinorCommunicationRulesAlert';
import { validatePassword, isPasswordValid } from '@/utils/passwordUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Under13RegistrationFlowProps {
  selectedRole: UserRole;
  onRegistrationComplete: (formData: any) => void;
  loading: boolean;
  error: string;
}

const Under13RegistrationFlow = ({ 
  selectedRole, 
  onRegistrationComplete, 
  loading, 
  error 
}: Under13RegistrationFlowProps) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    pin: '',
    confirmPin: '',
    age: '',
    teamCode: '',
    stateProvince: ''
  });
  const [referralCode, setReferralCode] = useState('');
  const [isReferralValid, setIsReferralValid] = useState(false);
  const [credentialError, setCredentialError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [isValidatingTeam, setIsValidatingTeam] = useState(false);

  const passwordRequirements = validatePassword(formData.password);
  const isPasswordStrong = isPasswordValid(passwordRequirements);

  const handleReferralCodeChange = (code: string, isValid: boolean) => {
    setReferralCode(code);
    setIsReferralValid(isValid);
  };

  const validateTeamCode = async (code: string): Promise<{ team_id: number; team_name: string } | null> => {
    try {
      const { data, error } = await supabase
        .rpc('validate_team_access_code', {
          p_access_code: code.toUpperCase(),
          p_user_id: null, // We'll validate without user creation first
          p_user_type: 'internal'
        });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; team_id?: number; team_name?: string };
      
      if (result.success) {
        return {
          team_id: result.team_id!,
          team_name: result.team_name!
        };
      } else {
        throw new Error(result.error || 'Invalid team code');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to validate team code');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCredentialError('');
    setIsValidatingTeam(true);

    try {
      // Validation
      if (!formData.username || !formData.password || !formData.pin || !formData.age || !formData.teamCode || !formData.stateProvince) {
        setCredentialError('Please fill in all fields including your state');
        return;
      }

      if (formData.username.length < 3) {
        setCredentialError('Username must be at least 3 characters long');
        return;
      }

      if (!isPasswordStrong) {
        setCredentialError('Password does not meet all requirements');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setCredentialError('Passwords do not match');
        return;
      }

      if (formData.pin.length < 4) {
        setCredentialError('PIN must be at least 4 characters long');
        return;
      }

      if (formData.pin !== formData.confirmPin) {
        setCredentialError('PINs do not match');
        return;
      }

      const age = parseInt(formData.age);
      if (isNaN(age) || age < 1 || age > 13) {
        setCredentialError('Age must be between 1 and 13');
        return;
      }

      if (formData.teamCode.length !== 8) {
        setCredentialError('Team access code must be 8 characters');
        return;
      }

      // If referral code is provided but invalid, show error
      if (referralCode && !isReferralValid) {
        setCredentialError('Please enter a valid referral code or leave it empty');
        return;
      }

      // Validate team code first
      const teamInfo = await validateTeamCode(formData.teamCode);
      
      if (!teamInfo) {
        setCredentialError('Invalid team access code');
        return;
      }

      // Hash credentials before storing
      const bcrypt = await import('bcryptjs');
      const passwordHash = await bcrypt.hash(formData.password, 10);
      const pinHash = await bcrypt.hash(formData.pin, 10);

      // Create user account with team membership
      const { data: newUser, error: userError } = await supabase
        .from('internal_users')
        .insert({
          username: formData.username,
          password_hash: passwordHash,
          pin_hash: pinHash,
          seed_phrase: '',
          seed_phrase_hash: '',
          role: 'player',
          age_group: '13-and-under'
        })
        .select()
        .single();

      if (userError) {
        if (userError.message.includes('duplicate') || userError.message.includes('unique')) {
          setCredentialError('Username already exists. Please choose a different username.');
        } else {
          setCredentialError('Failed to create account: ' + userError.message);
        }
        return;
      }

      // Create user profile with age information - include age_group and state for consistency
      await supabase
        .from('internal_user_profiles')
        .insert({
          user_id: newUser.id,
          age: age,
          age_group: '13-and-under',
          state_province: formData.stateProvince,
          bio: `Team member`,
          years_playing: 0,
          skill_level: 'Beginner'
        });

      // Now validate team code with the actual user ID
      const { data, error } = await supabase
        .rpc('validate_team_access_code', {
          p_access_code: formData.teamCode.toUpperCase(),
          p_user_id: newUser.id,
          p_user_type: 'internal'
        });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; team_id?: number; team_name?: string };

      if (result.success) {
        toast.success(`Successfully joined ${result.team_name}!`);
        
        // Complete registration
        onRegistrationComplete({
          ...formData,
          role: selectedRole,
          ageGroup: '13-and-under',
          age: age,
          referralCode: referralCode || undefined,
          teamInfo: {
            team_id: result.team_id!,
            team_name: result.team_name!
          },
          userId: newUser.id
        });
      } else {
        throw new Error(result.error || 'Failed to join team');
      }

    } catch (error: any) {
      console.error('Registration error:', error);
      setCredentialError(error.message || 'Registration failed');
    } finally {
      setIsValidatingTeam(false);
    }
  };

  const handleQRCodeScanned = (code: string) => {
    setFormData({ ...formData, teamCode: code });
    setShowScanner(false);
  };

  return (
    <div className="space-y-4">
      <Alert className="border-blue-500 bg-blue-900/50">
        <Users className="h-4 w-4" />
        <AlertDescription className="text-blue-200">
          <div className="space-y-2">
            <p className="font-medium">13 and Under Registration</p>
            <p className="text-sm">
              Create your account and join your team in one step. Fill in all fields below including the team access code from your coach.
            </p>
          </div>
        </AlertDescription>
      </Alert>

      <MinorCommunicationRulesAlert />

      {(credentialError || error) && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
          {credentialError || error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <InternalUsernameField
          value={formData.username}
          onChange={(username) => setFormData({ ...formData, username })}
        />

        <InternalPasswordSection
          password={formData.password}
          confirmPassword={formData.confirmPassword}
          onPasswordChange={(password) => setFormData({ ...formData, password })}
          onConfirmPasswordChange={(confirmPassword) => setFormData({ ...formData, confirmPassword })}
        />

        <InternalPinSection
          pin={formData.pin}
          confirmPin={formData.confirmPin}
          onPinChange={(pin) => setFormData({ ...formData, pin })}
          onConfirmPinChange={(confirmPin) => setFormData({ ...formData, confirmPin })}
        />

        <div>
          <Label htmlFor="age">Age</Label>
          <Input
            id="age"
            type="number"
            placeholder="Enter your age (1-13)"
            value={formData.age}
            onChange={(e) => setFormData({ ...formData, age: e.target.value })}
            min="1"
            max="13"
            className="bg-gray-800 border-gray-600 text-white"
          />
        </div>

        <StateSelector
          value={formData.stateProvince}
          onChange={(stateProvince) => setFormData({ ...formData, stateProvince })}
          required
          showNebraskaNote
        />

        <div>
          <Label htmlFor="teamCode">Team Access Code</Label>
          <div className="flex gap-2">
            <Input
              id="teamCode"
              type="text"
              placeholder="Enter 8-character code"
              value={formData.teamCode}
              onChange={(e) => setFormData({ ...formData, teamCode: e.target.value.toUpperCase() })}
              maxLength={8}
              className="text-center text-lg font-mono tracking-wider bg-gray-800 border-gray-600 text-white"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowScanner(true)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <QrCode className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Ask your coach for the team access code or QR code</p>
        </div>

        <ReferralCodeInput onReferralCodeChange={handleReferralCodeChange} />

        <Button
          type="submit"
          disabled={loading || isValidatingTeam || !isPasswordStrong || formData.teamCode.length !== 8}
          className="w-full bg-sports-green text-black hover:opacity-90"
        >
          {isValidatingTeam ? 'Creating Account...' : loading ? 'Completing Registration...' : 'Create Account & Join Team'}
        </Button>
      </form>

      <QRCodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onCodeScanned={handleQRCodeScanned}
      />
    </div>
  );
};

export default Under13RegistrationFlow;